import React, { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useRef, useState } from 'react';
import ticket from 'assets/img/ticket.png';
import { useStoreContext } from 'store/app';
import { useLocation, useNavigate } from 'react-router-dom';
import { CollectionLink } from './components/CollectionLink';
import {
    IShortLinkConfig,
    LootboxTypes,
    MainPageNavigation,
    ModalId,
    QuestTypes,
    ShortLinkSubType,
    ShortLinkType
} from 'src/types';
import { DragonOverlayContent } from './components/DragonOverlayContent';
import { FirstDayOverlayContent } from './components/FirstDayOverlayContent';
import { FirstDuplicateOverlayContent } from './components/FirstDuplicateOverlayContent';
import { SkippedDaysOverlayContent } from './components/SkippedDaysOverlayContent';
import './mainPage.styl';
import { UnopenedPresentsOverlay } from './components/UnopenedPresentsOverlay';
import { Navigation } from './components/Navigation';
import navTasks from 'assets/img/nav-icon-tasks.png';
import navPresents from 'assets/img/nav-icon-presents.png';
import navHelp from 'assets/img/nav-icon-help.png';
import { QuestsModal } from 'components/modal/questsModal';
import { HelpModal } from 'components/modal/helpModal';
import { DrawModal } from 'components/modal/draw/drawModal';
import { RewardsModal } from 'components/modal/rewardsModal';
import './mainPage.styl';
import { routes } from 'utils/router';
import { CompletedCollectionOverlayContent } from './components/CompletedCollectionOverlayContent';
import { RewardDetailsModal } from 'components/modal/rewardsModal/rewardDetailsModal';
import { QuestsGuideOverlayContent } from './components/QuestsGuideOverlayContent';
import { UnopenedLootboxOverlayContent } from './components/UnopenedLootboxOverlayContent';
import { OverlayStarAnimation } from 'components/layout/overlayStarAnimation';
import classNames from 'classnames';
import { CollectionEndOverlayContent } from './components/CollectionEndOverlayContent';
import { StorageKeys } from 'utils/storageKeys';
import { ResultsModal } from 'components/modal/draw/resultsModal';

interface IOverlayState {
    element: ReactElement;
    renderStar: boolean;
    centerStar?: boolean;
}

const defaultOverlayDuration = 5000;
const skippedDaysAmount = 3;

export const MainPage = observer(() => {
    const {
        isMob,
        userState,
        catalogConfig,
        firstDuplicate,
        setFirstDuplicate,
        userVariables,
        hasNewQuests,
        hasNewRewards,
        questsConfig,
        completeQuest,
        getVisitedDaysAmount,
        isVisited,
        setIsVisited,
        hasUnopenedRevolvers,
        shortLinkConfig,
        setShortLinkConfig,
        logOperation,
        setIsLoginsQuestJustComleted,
        loginsModalReward,
        setLoginsModalReward,
        setIsMainPage,
        checkOverlays: triggerCheckOverlays,
        setCheckOverlays,
        lotteryConfig,
        isCollectionFinished,
        isMainPartFinished,
        setUserVariable,
        isShowResults
    } = useStoreContext();

    const [overlayContent, setOverlayContent] = useState<IOverlayState>();
    const [showQuestsHelperAfterClose, setShowQuestsHelperAfterClose] = useState<Boolean>(false);
    const navigate = useNavigate();

    const { state } = useLocation();

    const overlayTimerRef = useRef<NodeJS.Timeout>(undefined);

    const [preopenId, setPreopenId] = useState<string>();

    const navigation: MainPageNavigation[] = [
        {
            id: 'draw',
            label: 'nav.link.draw',
            iconSrc: ticket,
            modalId: ModalId.Draw,
            ModalElem: isShowResults ? ResultsModal : DrawModal
        },
        {
            id: 'quests',
            label: 'title.quests',
            iconSrc: navTasks,
            modalId: ModalId.Quests,
            ModalElem: QuestsModal,
            isGreenDotVisible: hasNewQuests
        },
        {
            id: 'presents',
            label: 'nav.link.presents',
            iconSrc: navPresents,
            modalId: ModalId.Rewards,
            ModalElem: RewardsModal,
            isGreenDotVisible: hasNewRewards
        },
        {
            id: 'help',
            label: 'nav.link.help',
            iconSrc: navHelp,
            modalId: ModalId.Help,
            ModalElem: HelpModal
        }
    ];

    const [openNavModal, setOpenNavModal] = useState(() =>
        navigation.reduce((acc, elem) => {
            if (elem.modalId) {
                acc[elem.modalId] = false;
            }

            return acc;
        }, {} as Record<ModalId, boolean>)
    );

    const onOpenNavModal = (key: ModalId) => {
        setOpenNavModal((prev) => ({
            ...prev,
            [key]: true
        }));

        if (isVisited) {
            setIsVisited(false);
            setOverlayContent(undefined);
        }
    };

    const onCloseNavModal = useCallback(
        (key: ModalId) => {
            setOpenNavModal((prev) => ({
                ...prev,
                [key]: false
            }));

            if (showQuestsHelperAfterClose) {
                setShowQuestsHelperAfterClose(false);
                setOverlayContent({
                    element: (
                        <QuestsGuideOverlayContent
                            navigation={
                                <Navigation
                                    items={navigation}
                                    openNavModal={openNavModal}
                                    onOpenNavModal={onOpenNavModal}
                                    onCloseNavModal={onCloseNavModal}
                                    highLightedItem={ModalId.Quests}
                                />
                            }
                            isEnd
                        />
                    ),
                    renderStar: false
                });
            }
        },
        [showQuestsHelperAfterClose]
    );

    useEffect(() => {
        setIsMainPage(true);

        return () => {
            setIsMainPage(false);
        };
    }, [setIsMainPage]);

    useEffect(() => {}, [questsConfig]);

    const checkOverlays = useCallback(() => {
        if (isShowResults) {
            onOpenNavModal(ModalId.Draw);
            return true;
        }

        const completedUnopenedCollection =
            !isCollectionFinished &&
            !isMainPartFinished &&
            lotteryConfig &&
            catalogConfig &&
            userState &&
            userState.collections?.find((collectionState) => {
                if (collectionState.snakes && collectionState.snakes.length === 3) {
                    const collectionConfig = catalogConfig.collections.find(
                        (colConfig) => colConfig.id === collectionState.id
                    );
                    return collectionConfig && !!lotteryConfig[collectionConfig.lotteryId].availableTicket;
                }
                return false;
            });

        if (completedUnopenedCollection) {
            setOverlayContent({
                element: <CompletedCollectionOverlayContent collection={completedUnopenedCollection} />,
                renderStar: true
            });
            return true;
        }

        const hasUnopenedDragonBonus =
            !isCollectionFinished &&
            !isMainPartFinished &&
            userState?.unopened_bonus_lootboxes?.some((x) => x.key === LootboxTypes.DRAGON);
        if (hasUnopenedDragonBonus) {
            logOperation('dragon_snake', 'render');
            setOverlayContent({ element: <DragonOverlayContent />, renderStar: true });
            return true;
        }

        const quest = questsConfig?.tasks.find((x) => x.newYearType === QuestTypes.LOGINS);
        if (quest && quest.done !== 1 && getVisitedDaysAmount() >= (quest.goal || 14)) {
            completeQuest(quest.key);
            setIsLoginsQuestJustComleted(true);
            return true;
        }

        const hasUnopenedMissionBonus =
            !isCollectionFinished &&
            !isMainPartFinished &&
            userState?.unopened_bonus_lootboxes?.find((x) => x.key !== LootboxTypes.DRAGON);
        if (hasUnopenedMissionBonus) {
            setOverlayContent({
                element: <UnopenedLootboxOverlayContent lootbox={hasUnopenedMissionBonus} />,
                renderStar: true
            });
            return true;
        }

        const isFirstDay = !isCollectionFinished && !isMainPartFinished && userVariables?.visited !== 'true';
        if (isFirstDay) {
            setOverlayContent({ element: <FirstDayOverlayContent />, renderStar: true, centerStar: true });
            overlayTimerRef.current = setTimeout(() => {
                setOverlayContent({
                    element: (
                        <QuestsGuideOverlayContent
                            navigation={
                                <Navigation
                                    items={navigation}
                                    openNavModal={openNavModal}
                                    onOpenNavModal={onOpenNavModal}
                                    onCloseNavModal={onCloseNavModal}
                                    highLightedItem={ModalId.Quests}
                                />
                            }
                        />
                    ),
                    renderStar: false
                });
            }, defaultOverlayDuration);
            return true;
        }

        if (!isCollectionFinished && !isMainPartFinished && firstDuplicate) {
            setOverlayContent({
                element: <FirstDuplicateOverlayContent duplicateSnakeId={firstDuplicate} />,
                renderStar: false
            });
            setFirstDuplicate(undefined);
            return true;
        }

        const isNotifiedAboutEnd = userVariables?.end_notified === 'true';
        if (isMainPartFinished && !isNotifiedAboutEnd) {
            setUserVariable(StorageKeys.END_NOTIFIED, 'true');
            setOverlayContent({
                element: (
                    <CollectionEndOverlayContent
                        navigation={
                            <Navigation
                                items={navigation}
                                openNavModal={openNavModal}
                                onOpenNavModal={onOpenNavModal}
                                onCloseNavModal={onCloseNavModal}
                                highLightedItem={ModalId.Draw}
                            />
                        }
                    />
                ),
                renderStar: false
            });

            setShowQuestsHelperAfterClose(true);
            return true;
        }

        const isVisibleUnopenedPresentsOverlay = hasUnopenedRevolvers() && isVisited;
        if (isVisibleUnopenedPresentsOverlay) {
            setOverlayContent({
                element: (
                    <UnopenedPresentsOverlay
                        navigation={
                            <Navigation
                                items={navigation}
                                openNavModal={openNavModal}
                                onOpenNavModal={onOpenNavModal}
                                onCloseNavModal={onCloseNavModal}
                                highLightedItem={ModalId.Rewards}
                            />
                        }
                    />
                ),
                renderStar: false
            });
            return true;
        }

        const hasVisited = userState && userState.visited_days && userState.visited_days.length;
        const lastVisitedDay = (hasVisited && userState.visited_days?.[userState.visited_days.length - 1]) || 0;
        const today = userState?.day || 0;
        const isSkippedDays = lastVisitedDay < today && lastVisitedDay - today >= skippedDaysAmount;

        if (!isCollectionFinished && !isMainPartFinished && isSkippedDays) {
            setOverlayContent({
                element: (
                    <SkippedDaysOverlayContent
                        navigation={
                            <Navigation
                                items={navigation}
                                openNavModal={openNavModal}
                                onOpenNavModal={onOpenNavModal}
                                onCloseNavModal={onCloseNavModal}
                                highLightedItem={ModalId.Quests}
                            />
                        }
                    />
                ),
                renderStar: false
            });
            return true;
        }

        return false;
    }, [
        firstDuplicate,
        userState,
        lotteryConfig,
        catalogConfig,
        isCollectionFinished,
        isMainPartFinished,
        isShowResults
    ]);

    const navigateToRightShortlink = ({ type, subtype, id }: IShortLinkConfig) => {
        if (type === ShortLinkType.TAB) {
            if (subtype === ShortLinkSubType.COLLECTIONS) {
                navigate(routes.collection);
            }

            if (subtype === ShortLinkSubType.DRAW) {
                onOpenNavModal(ModalId.Draw);
            }

            if (subtype === ShortLinkSubType.MISSIONS) {
                onOpenNavModal(ModalId.Quests);
            }

            if (subtype === ShortLinkSubType.REWARDS) {
                onOpenNavModal(ModalId.Rewards);
            }

            if (subtype === ShortLinkSubType.HELP) {
                onOpenNavModal(ModalId.Help);
            }
        }

        if (type === ShortLinkType.MISSION) {
            setPreopenId(id);
            onOpenNavModal(ModalId.Quests);
        }

        if (type === ShortLinkType.REWARD) {
            setPreopenId(id);
            onOpenNavModal(ModalId.Rewards);
        }
    };

    useEffect(() => {
        const statTimeout = setTimeout(() => logOperation('main_page', 'render'), 100);
        const hasOverlay = checkOverlays();
        if (!hasOverlay && shortLinkConfig) {
            navigateToRightShortlink(shortLinkConfig);
        }
        setShortLinkConfig();

        return () => {
            clearTimeout(statTimeout);
            overlayTimerRef.current && clearTimeout(overlayTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (triggerCheckOverlays && userState?.unopened_bonus_lootboxes?.length) {
            checkOverlays();
            setCheckOverlays(false);
        }
    }, [triggerCheckOverlays, userState]);

    useEffect(() => {
        if (state?.openPresentModal) {
            onOpenNavModal(ModalId.Rewards);
        }
    }, [state?.openPresentModal]);

    useEffect(() => {
        if (state?.openDraw) {
            onOpenNavModal(ModalId.Draw);
        }
    }, [state?.openDraw]);

    return (
        <div className="main-page">
            <div className="main-page-top">
                <div
                    className="main-page-tickets-container"
                    onClick={() => {
                        logOperation('main_page', 'click', 'ticket');
                        onOpenNavModal(ModalId.Draw);
                    }}
                >
                    <span className="main-page-tickets-count">{userState?.tickets?.length ?? 0}</span>
                    <img className="main-page-tickets-icon" src={ticket} alt="купон" />
                </div>
                {!isMob && <CollectionLink />}
            </div>
            <div className="main-page-bottom">
                {isMob && <CollectionLink />}
                <div className="main-page-navigation">
                    <Navigation
                        items={navigation}
                        openNavModal={openNavModal}
                        onOpenNavModal={onOpenNavModal}
                        onCloseNavModal={onCloseNavModal}
                        preopenId={preopenId}
                        onPreopenOpened={() => setPreopenId(undefined)}
                    />
                </div>
                <div className="main-page-bottom-layer" />
            </div>

            {overlayContent && (
                <div className="main-page-info-overlay">
                    {overlayContent.renderStar ? (
                        <OverlayStarAnimation
                            className={classNames('main-page-info-overlay-star', {
                                centred: overlayContent.centerStar
                            })}
                        />
                    ) : null}
                    <div className="main-page-info-overlay-content">{overlayContent.element}</div>
                </div>
            )}
            {loginsModalReward ? (
                <RewardDetailsModal
                    reward={loginsModalReward!}
                    isOpened={true}
                    onClose={() => setLoginsModalReward(undefined)}
                />
            ) : null}
        </div>
    );
});
